import { useState, useCallback } from 'react';
import i18n from 'i18next';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import shallow from 'zustand/shallow';
import native from '@native';
import { getQueryString, http, isInApp, noop } from '@shared';
import { loginApi } from '@api';
import {
  TOKEN,
  REFRESH_TOKEN,
  USER_INFO,
  LOCALE,
  SECONDARY_CERTIFICATION_SIGN,
  SECONDARY_CERTIFICATION_GET_CODE_TIME,
} from '@constant';
import { useUserState } from '@root/state';
import { ICertificationCode, loginCertificationCode } from '@root/api/login';
import loggerOperator from '@root/logger/loggerOperator';

const REGEXP_EMAIL =
  /^[\w!#$%&'*+/=?^_`{|}~-]+(?:\.[\w!#$%&'*+/=?^_`{|}~-]+)*@([a-zA-Z0-9_-]+[-.])+([a-zA-Z0-9_-]){2,10}$/;
const useLogin = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loginState, setUser, setLoginState, resetLoginState } = useUserState(
    (state) => ({
      loginState: state.loginState,
      setUser: state.setUser,
      setLoginState: state.setLoginState,
      resetLoginState: state.resetLoginState,
    }),
    shallow,
  );
  const [signing, setSigning] = useState(false);
  const { username, password, errorInfo } = loginState;
  const [lang, setLang] = useState('zh-CN');

  const _validateUsername = useCallback(
    (v?: string) => {
      const success = REGEXP_EMAIL.test(v || username);
      if (success) {
        setLoginState({ errorInfo: '' });
      } else {
        setLoginState({ errorInfo: t('email_error') });
      }
      return success;
    },
    [setLoginState, t, username],
  );

  const onLoginSuccess = useCallback(
    (data: any) => {
      const {
        accessToken,
        id,
        username: _username,
        nickname,
        refreshToken,
        expiresIn,
      } = data || noop.EMPTY_OBJECT;

      if (accessToken) {
        http.setOptions({
          headers: {
            'access-token': accessToken,
            token: accessToken,
          },
        });

        http.setParameters({
          refreshToken,
          expiresIn,
          expiresTimestamp: Date.now() + expiresIn * 1000,
        });

        localStorage.setItem(TOKEN, accessToken);
        localStorage.setItem(REFRESH_TOKEN, refreshToken);
      }

      const userInfo = {
        id,
        username: _username,
        nickname,
      };

      window.localStorage.setItem(USER_INFO, JSON.stringify(userInfo));

      if (loggerOperator && loggerOperator.instance) {
        (loggerOperator.instance as any).loginWithKey('$identity_login_id', id);
        (loggerOperator.instance as any).registerPage({
          is_login: true,
        });
      }

      setUser(userInfo);

      resetLoginState();

      setSigning(false);

      let redirectUrl = getQueryString('redirectUrl');
      redirectUrl = redirectUrl ? decodeURIComponent(redirectUrl) : '';

      if (redirectUrl) {
        if (redirectUrl.startsWith('/subscription/')) {
          const [_pathname] = redirectUrl.match(/(\/subscription\/\w+)/) || [];
          const [_search] = redirectUrl.match(/\?([\w\W]+)/) || [];
          if (_pathname) {
            navigate(`${_pathname}${_search || ''}`, { replace: true });
          }
          return;
        }
        if (redirectUrl.startsWith('/promotions/selectDevice')) {
          navigate('/promotions/selectDevice', { replace: true });
          return;
        }
      }

      navigate('/', { replace: true });
    },
    [navigate, resetLoginState, setUser],
  );

  const onLogin = useCallback(() => {
    setSigning(true);
    loginApi({ username, password })
      .then((data: any) => {
        if (data?.secondaryCertificationSign) {
          window.localStorage.setItem(
            SECONDARY_CERTIFICATION_SIGN,
            data.secondaryCertificationSign,
          );
          window.localStorage.removeItem(SECONDARY_CERTIFICATION_GET_CODE_TIME);

          navigate('/login/certification');
          return;
        }
        onLoginSuccess(data);
      })
      .catch((reason) => {
        const { data } = reason.response || {};
        let msg = t('login_failed_try_again');
        if (reason.response?.status === 400 && data.desc) {
          msg = data.desc;
        }
        setSigning(false);
        setLoginState({ errorInfo: msg });
      });
  }, [navigate, onLoginSuccess, password, setLoginState, t, username]);

  const loginWithSecondCertification = useCallback(
    async (request: ICertificationCode) => {
      try {
        const response = await loginCertificationCode(request);
        onLoginSuccess(response);
        return response;
      } catch (e) {
        return Promise.reject(e);
      }
    },
    [onLoginSuccess],
  );

  const onClickTerms = useCallback(() => {
    if (isInApp && window.WebViewJavascriptBridge) {
      native.system.openBrowser(
        'https://www.aidot.com/page/supplemental-terms',
      );
    } else {
      window.open('https://www.aidot.com/page/supplemental-terms', '_blank');
    }
  }, []);

  const onSelectLang = useCallback((v: string) => {
    i18n.changeLanguage(v);
    setLang(v);
    localStorage.setItem(LOCALE, v);
  }, []);

  const onChangeUsername = useCallback(
    (e: any) => {
      const _v = e.target.value;
      setLoginState({ username: _v });
      _validateUsername(_v);
    },
    [_validateUsername, setLoginState],
  );

  const onChangePassword = useCallback(
    (e: any) => {
      setLoginState({ password: e.target.value });
      _validateUsername();
    },
    [_validateUsername, setLoginState],
  );

  const onBlurUsername = useCallback(() => {
    _validateUsername();
  }, [_validateUsername]);

  return {
    lang,
    signing,
    username,
    password,
    errorInfo,
    onClickTerms,
    onLogin,
    loginWithSecondCertification,
    onSelectLang,
    onChangeUsername,
    onBlurUsername,
    onChangePassword,
  };
};

export default useLogin;
